import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Tabs } from 'antd';
import { useCompanyForm } from './hooks/useCompanyForm';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Addresses from './components/Tab2Addresses';
import Tab3Contacts from './components/Tab3Contacts';
import Tab4Credit from './components/Tab4Credit';
import Tab5OrderCustomers from './components/Tab5OrderCustomers';

const P1104Form: React.FC = () => {
  const form = useCompanyForm();
  const [searchText, setSearchText] = useState('');
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyCode, setCopyCode] = useState('');

  const handleDelete = () => {
    if (!form.current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${form.current.f104_01} ${form.current.f104_12}」及其所有子表資料？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: form.doDelete,
    });
  };

  const handleCopy = () => {
    if (!form.current) return;
    setCopyCode(''); setCopyModalOpen(true);
  };

  const handleCopyConfirm = async () => {
    if (!copyCode.trim()) return;
    await form.doCopy(copyCode.trim());
    setCopyModalOpen(false);
  };

  const modeLabel = form.mode === 'add' ? '新增' : form.mode === 'edit' ? '修改' : '瀏覽';

  const tabItems = [
    {
      key: '1', label: '基本資料',
      children: (
        <Tab1BasicInfo isEditing={form.isEditing} isAddMode={form.mode === 'add'}
          code={form.code} setCode={form.setCode}
          category={form.category} setCategory={form.setCategory}
          shortName={form.shortName} setShortName={form.setShortName}
          fullName={form.fullName} setFullName={form.setFullName}
          enName={form.enName} setEnName={form.setEnName}
          countryCode={form.countryCode} setCountryCode={form.setCountryCode}
          taxId={form.taxId} setTaxId={form.setTaxId}
          email={form.email} setEmail={form.setEmail}
          website={form.website} setWebsite={form.setWebsite}
          perfCustomer={form.perfCustomer} setPerfCustomer={form.setPerfCustomer}
          poRequired={form.poRequired} setPoRequired={form.setPoRequired}
          personInCharge={form.personInCharge} setPersonInCharge={form.setPersonInCharge}
          bankAccount={form.bankAccount} setBankAccount={form.setBankAccount}
          oldCodeFlag={form.oldCodeFlag} setOldCodeFlag={form.setOldCodeFlag}
          custCategory={form.custCategory} setCustCategory={form.setCustCategory}
          payType={form.payType} setPayType={form.setPayType}
          taxRate={form.taxRate} setTaxRate={form.setTaxRate}
        />
      ),
    },
    {
      key: '2', label: '地址',
      children: (
        <Tab2Addresses isEditing={form.isEditing} addresses={form.addresses}
          setAddresses={form.setAddresses} companyCode={form.code} />
      ),
    },
    {
      key: '3', label: '聯絡人',
      children: (
        <Tab3Contacts isEditing={form.isEditing} contacts={form.contacts}
          setContacts={form.setContacts} companyCode={form.code}
          businessCards={form.businessCards} setBusinessCards={form.setBusinessCards} />
      ),
    },
    {
      key: '4', label: '信用狀況',
      children: (
        <Tab4Credit isEditing={form.isEditing} credit={form.credit}
          setCredit={form.setCredit} category={form.category}
          remark={form.remark} setRemark={form.setRemark} />
      ),
    },
    {
      key: '5', label: '下單客戶',
      children: (
        <Tab5OrderCustomers isEditing={form.isEditing} orderCustomers={form.orderCustomers}
          setOrderCustomers={form.setOrderCustomers} companyCode={form.code} />
      ),
    },
  ];

  return (
    <Spin spinning={form.loading}>
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          公司資料建檔 (pw1104f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {form.records.length > 0 ? `${form.currentIdx + 1} / ${form.records.length}` : '無資料'}
            {form.current && ` — ${form.current.f104_01} ${form.current.f104_12}`}
          </span>
        </h2>

        {/* PK Display */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-500">公司代號:</span>
          <Input value={form.current?.f104_01 || (form.mode === 'add' ? '(自動/輸入)' : '')} disabled style={{ width: 150 }} />
        </div>

        {/* Quick Search + Navigation */}
        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="輸入公司代號查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && form.quickSearch(searchText.trim())}
            disabled={form.isEditing} style={{ width: 250 }} />
          <Button onClick={() => searchText.trim() && form.quickSearch(searchText.trim())}
            disabled={form.isEditing}>查詢</Button>
          <span className="mx-2" />
          <Button size="small" onClick={form.navFirst} disabled={form.isEditing || form.records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={form.navPrev} disabled={form.isEditing || form.currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={form.navNext} disabled={form.isEditing || form.currentIdx >= form.records.length - 1}>&gt;</Button>
          <Button size="small" onClick={form.navLast} disabled={form.isEditing || form.records.length === 0}>&gt;|</Button>
        </div>

        {/* Tabs */}
        <Tabs items={tabItems} defaultActiveKey="1" />

        {/* Audit info */}
        {form.detail && form.mode === 'browse' && (
          <div className="text-xs text-gray-400 mt-2">
            建立: {form.detail.f104_91} | 日期: {form.detail.f104_92 || '-'} | 修改: {form.detail.f104_93}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 mt-4">
          {!form.isEditing ? (
            <>
              <Button type="primary" onClick={form.startAdd}>新增</Button>
              <Button onClick={form.startEdit} disabled={!form.current || form.current.f104_35}>修改</Button>
              <Button onClick={handleCopy} disabled={!form.current}>複製</Button>
              <Button danger onClick={handleDelete} disabled={!form.current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={form.save}>儲存</Button>
              <Button onClick={form.cancel}>取消</Button>
            </>
          )}
        </div>

        <Modal title="複製公司" open={copyModalOpen} onOk={handleCopyConfirm}
          onCancel={() => setCopyModalOpen(false)} okText="確定" cancelText="取消">
          <p className="mb-2">來源: {form.current?.f104_01} {form.current?.f104_12}</p>
          <Input placeholder="請輸入新公司代號" value={copyCode}
            onChange={e => setCopyCode(e.target.value)} maxLength={12} />
        </Modal>
      </div>
    </Spin>
  );
};

export default P1104Form;
